import React from 'react';
import { Layer } from '../ConfigPanel';
import Canvg from 'canvg';
import { loadSVGs } from '../../utils/svgs';
import style from './Output.module.scss';
import classnames from 'classnames';

interface Props {
  layers: Layer[];
  globalColors: string[];
}

function getDimensionObject(node: HTMLElement | null) {
  if (!node)
    return {
      width: null,
      height: null,
      top: null,
      left: null,
      right: null,
      bottom: null,
    };
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    top: rect.x,
    left: rect.y,
    right: rect.right,
    bottom: rect.bottom,
  };
}

export function Output(props: Props) {
  const { layers, globalColors } = props;
  const canvas = React.useRef<HTMLCanvasElement>(null);
  const container = React.useRef<HTMLDivElement>(null);
  const [{ width, height }, setDimensions] = React.useState(
    getDimensionObject(null)
  );
  const size = Math.min(width || 0, height || 0);
  React.useLayoutEffect(() => {
    if (container.current) {
      const measure = () => {
        window.requestAnimationFrame(() => {
          const dim = getDimensionObject(container.current);
          setDimensions(dim);
        });
      };
      measure();
      window.addEventListener('resize', measure);

      return () => {
        window.removeEventListener('resize', measure);
      };
    }
  }, [container]);

  // TODO: can't currently ever shrink
  React.useEffect(() => {
    async function draw() {
      if (!canvas.current) return;
      const ctx = canvas.current.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, size, size);
      const svgs = await loadSVGs(layers, globalColors);

      await Promise.all(
        svgs
          .map(svg =>
            Canvg.fromString(ctx, svg, {
              ignoreClear: true,
              ignoreMouse: true,
            })
          )
          .reverse()
          .map(c => c.render())
      );
    }

    draw();
  }, [layers, canvas, size, globalColors]);

  // TODO: for final output render, we will probably want a fixed size so we can still get high-res output on small devices
  return (
    <div ref={container} className={classnames(style.canvas)}>
      {size ? <canvas ref={canvas} width={size} height={size} /> : null}
    </div>
  );
}
